import styled from '@emotion/styled';
import { Flex, Layout as LLayout, Menu, theme, Typography } from 'antd';
import { Heading, Store } from 'lucide-react';
import { type ReactNode, useContext, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/auth-context-provider';
import usePages from '../hooks/use-pages';
import { Roles } from '../types';

const StyledMenu = styled(Menu)`
  & .ant-menu-item-group {
    padding-top: 0.4rem;
  }
  & .ant-menu-item-group-title {
    font-size: 0.8rem;
    padding-bottom: 0;
  }
  & .ant-menu-item-divider {
    display: none;
  }
  &.ant-menu-inline-collapsed {
    .ant-menu-item {
      display: flex;
      align-items: center;

      .ant-menu-title-content {
        width: 0;
        margin: 0;
      }
    }
    .ant-menu-item-divider:not(.ant-menu-item-divider:first-of-type) {
      display: block;
      margin-block-start: 0;
    }
    .ant-menu-item-group {
      padding: 0;
    }
    .ant-menu-item-group-title {
      display: none;
    }
  }
  & .ant-menu-item .ant-menu-item-extra .ant-badge-dot {
    background: var(--ant-menu-item-color);
  }
  & .ant-menu-item-selected .ant-menu-item-extra .ant-badge-dot {
    background: var(--ant-menu-item-selected-color);
  }
`;

const SIDER_WIDTH = 250;

interface SiderProps {
  isSiderCollapsed: boolean;
  onSiderCollapseChange: (value: boolean) => void;
}
const dividerItem = {
  type: 'divider',
};
const LayoutSider = (props: SiderProps): ReactNode => {
  const location = useLocation();
  const pages = usePages();
  const { session } = useContext(AuthContext);

  const { token } = theme.useToken();
  // const organizationContext = useContext(OrganizationContext);
  const { onSiderCollapseChange, isSiderCollapsed } = props;

  const sideBarItems = useMemo(
    () => [
      {
        key: 'general',
        label: 'General',
        type: 'group',
        children: Object.entries(pages)
          .filter(([_, page]) => page.group === 'general')
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'orders',
        label: 'Orders',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) =>
              page.group === 'orders' &&
              page.roles?.includes(session?.claims.role ?? Roles.ROLE_CUSTOMER_STANDARD)
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'billing',
        label: 'Billing',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) =>
              page.group === 'billing' &&
              (session?.claims.role === Roles.ROLE_HHC_ADMIN ||
                session?.claims.role === Roles.ROLE_CUSTOMER_ADMIN)
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'reports',
        label: 'Reports',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) => page.group === 'reports' && session?.claims.role === Roles.ROLE_HHC_ADMIN
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'scheduling',
        label: 'Scheduling',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) =>
              page.group === 'scheduling' && session?.claims.role === Roles.ROLE_HHC_ADMIN
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'planning',
        label: 'Meals Planning',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) =>
              page.group === 'planning' && session?.claims.role === Roles.ROLE_HHC_ADMIN
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'meals-settings',
        label: 'Meals Settings',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) =>
              page.group === 'meals-settings' && session?.claims.role === Roles.ROLE_HHC_ADMIN
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
      {
        key: 'management',
        label: 'Management',
        type: 'group',
        children: Object.entries(pages)
          .filter(
            ([_, page]) =>
              page.group === 'management' &&
              page.roles?.includes(session?.claims.role ?? Roles.ROLE_CUSTOMER_STANDARD)
          )
          .map(([path, page]) => ({
            ...page,
            key: path,
            label: <Link to={path}>{page.name}</Link>,
            children: undefined,
          })),
      },
    ],
    [pages]
  );
  const selectedKey = useMemo(
    () =>
      Object.keys(pages)
        .filter((path) => location.pathname.startsWith(path))
        .map((path) => path)
        .sort()
        .pop() ?? '/',
    [location.pathname]
  );

  return (
    <LLayout.Sider
      id="nav-sider"
      breakpoint="xl"
      collapsedWidth="60"
      width={SIDER_WIDTH}
      style={{
        overflowX: 'clip',
        minHeight: '100%',
        borderInlineEnd: `var(--ant-line-width) var(--ant-line-type) var(--ant-color-border-secondary)`,
      }}
      trigger={null}
      collapsed={isSiderCollapsed}
      onCollapse={(isCollapsed) => {
        onSiderCollapseChange(isCollapsed);
      }}
    >
      <Flex
        gap={token.paddingSM}
        style={{
          padding: token.paddingSM,
          borderBlockEnd: `${token.lineWidth}px ${token.lineType} ${token.colorBorder}`,
          width: SIDER_WIDTH,
        }}
      >
        <Flex
          align="center"
          justify="center"
          style={{
            backgroundColor: token.colorPrimary,
            aspectRatio: '1 / 1',
            height: '2.35rem',
            borderRadius: token.borderRadius,
          }}
        >
          {session?.claims.role === Roles.ROLE_CUSTOMER_ADMIN ? (
            <Store style={{ color: token.colorPrimaryText, width: '1.2rem', height: '1.2rem' }} />
          ) : (
            <Heading style={{ color: token.colorPrimaryText, width: '1.2rem', height: '1.2rem' }} />
          )}
        </Flex>
        <Flex vertical>
          <Typography.Title
            level={5}
            style={{
              margin: 0,
              lineHeight: 0.9,
            }}
          >
            {session?.claims.customerName ?? 'Hoffman House'}
          </Typography.Title>
          <Typography.Text type="secondary">
            {Roles[session?.claims.role ?? 0].toString().split('_')[2]}
          </Typography.Text>
        </Flex>
      </Flex>
      <StyledMenu
        style={{ border: 0, overflowY: 'auto', scrollbarWidth: 'none' }}
        mode="inline"
        selectedKeys={[selectedKey]}
        items={sideBarItems
          .filter((group) => group.children?.length > 0)
          .reduce<any[]>((cum, el: any, _idx, _arr) => cum.concat([dividerItem, el]), [])}
      />
    </LLayout.Sider>
  );
};

export default LayoutSider;
